// frontend/src/components/IntroOfferCard.js
//
// INTRO-OFFER-001-D — customer-facing Single Sessions countdown card.
//
// Renders one of 5 lifecycle states (+ 2 emphasis variants + §3.2.6b
// post-cancel sub-variant) driven by the `introOffer` block surfaced
// additively on /api/profile (tech doc §"GET /api/profile").
//
// Spec sources (locked):
//   docs/design/INTRO-OFFER-001-design.md
//     §3   Surface 1 — Single Sessions page CTA (placement + states)
//     §3.2 Visual states (5 + 2 emphasis variants + §3.2.6b sub-variant)
//     §3.4 Transition rules
//     §3.5 Loading + error states (graceful fallback — never blocks page)
//     §3.6 Accessibility (aria-label, color-never-sole-differentiator,
//          touch-target minimums)
//     §6   Typography
//     §7   Color tokens
//     §9   Animations + prefers-reduced-motion
//     §10.1 Visual DoD
//   docs/architecture/INTRO-OFFER-001-tech.md
//     §"GET /api/profile" — response shape including server-rendered
//      `displayCopy.primary` + `displayCopy.secondary` (consumed VERBATIM
//      per Scenario 12 — FE does not re-format dates).
//   docs/backlog/INTRO-OFFER-001-D-customer-single-sessions-copy.md
//     Scenarios 1-14 + DoD.
//
// Production-safety:
//   - Renders nothing when `introOffer` is null/undefined (legacy customer
//     OR feature flag OFF on backend OR partial response). The Single
//     Sessions page falls back to its existing layout. Same graceful-fallback
//     posture as ESIGN status indicator.
//   - All API timestamps are Phoenix-MST-anchored; we consume `displayCopy`
//     strings verbatim where available (Scenario 12), with FE-side
//     Phoenix-MST formatting as a defensive fallback when the server omits
//     `displayCopy` for any reason.
//   - Click-to-claim invokes POST /api/intro-offer/claim and refreshes the
//     profile to pick up the updated state. On 400 CLAIM_WINDOW_EXPIRED the
//     parent is asked to refresh too — the BE flips status defensively in
//     the same transaction (tech doc §"POST /api/intro-offer/claim").

import React, { useState } from "react";
import { introOfferAPI } from "../services/api";
import "./IntroOfferCard.css";

// Phoenix never observes DST so "MST" is always correct (CLAUDE.md mandate
// — "All times displayed in America/Phoenix timezone regardless of device
// locale. Always label times 'MST' in the UI.").
const PHOENIX_TZ = "America/Phoenix";

/**
 * Defensive client-side Phoenix-MST date formatter. Used ONLY when the
 * server omits `displayCopy` (e.g., partial response). Server-rendered
 * `displayCopy` is the source of truth per tech doc + Scenario 12.
 *
 * @param {string} iso ISO 8601 timestamp
 * @returns {string} e.g., "Apr 26, 2026 MST"
 */
function formatPhoenixDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const datePart = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: PHOENIX_TZ,
  });
  return `${datePart} MST`;
}

/**
 * Defensive client-side Phoenix-MST date-time formatter. Used for the
 * pending-booking time row (§3.2.3) which is sourced from
 * `pendingBookingStartTime` rather than from server-rendered displayCopy.
 *
 * @param {string} iso ISO 8601 timestamp
 * @returns {string} e.g., "Apr 24, 2026 — 2:00 PM MST"
 */
function formatPhoenixDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const datePart = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: PHOENIX_TZ,
  });
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: PHOENIX_TZ,
  });
  return `${datePart} — ${timePart} MST`;
}

/**
 * Compute days-remaining vs a deadline, rounded UP (so "1 day left" covers
 * the final 24h window per spec §3.2.1b). Negative result means already
 * past — caller should treat as expired regardless.
 *
 * @param {string|Date} deadlineIso
 * @param {Date} [now]
 * @returns {number}
 */
function daysRemaining(deadlineIso, now) {
  if (!deadlineIso) return 0;
  const deadline = new Date(deadlineIso).getTime();
  const t = (now || new Date()).getTime();
  if (Number.isNaN(deadline)) return 0;
  return Math.max(0, Math.ceil((deadline - t) / (24 * 60 * 60 * 1000)));
}

/**
 * Hours-remaining for the < 24h emphasis branch (Scenario 2: "12 hours left").
 *
 * @param {string|Date} deadlineIso
 * @param {Date} [now]
 * @returns {number}
 */
function hoursRemaining(deadlineIso, now) {
  if (!deadlineIso) return 0;
  const deadline = new Date(deadlineIso).getTime();
  const t = (now || new Date()).getTime();
  if (Number.isNaN(deadline)) return 0;
  return Math.max(0, Math.ceil((deadline - t) / (60 * 60 * 1000)));
}

// -------------------------------------------------------------------------
// Icons (24pt). All decorative — text labels carry meaning per spec §3.6.
// -------------------------------------------------------------------------

const GiftIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M20 12v8H4v-8M2 7h20v5H2V7zM12 22V7M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <path
      d="M7 12l3 3 7-7"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="5"
      width="18"
      height="16"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    <path d="M3 9h18" stroke="currentColor" strokeWidth="2" />
    <path
      d="M8 3v4M16 3v4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    aria-hidden="true"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M8 8l8 8M16 8l-8 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// -------------------------------------------------------------------------
// State renderers. Each takes the `introOffer` block + handlers and returns
// JSX for one of the 5 (+ emphasis + sub-variant) states. The wrapper below
// dispatches on `effectiveStatus` and derives the about-to-expire emphasis.
// -------------------------------------------------------------------------

/**
 * Skeleton loading state per spec §3.5. Same card chrome; pulse drives the
 * affordance. Never blocks page render (parent renders other session-type
 * cards regardless).
 */
const SkeletonState = () => (
  <section
    className="intro-offer-card intro-offer-card--skeleton"
    aria-label="Intro offer status"
    aria-busy="true"
    role="status"
  >
    <div className="intro-offer-card__skeleton-line" />
    <div className="intro-offer-card__skeleton-line intro-offer-card__skeleton-line--subline" />
    <div className="intro-offer-card__skeleton-line intro-offer-card__skeleton-line--cta" />
  </section>
);

/**
 * Eligible state — claim window open. §3.2.1 + §3.2.1b emphasis when
 * daysRemaining <= 1 (Scenario 2).
 */
const EligibleState = ({
  introOffer,
  isUrgent,
  isSubDay,
  onClaim,
  claiming,
  claimError,
}) => {
  const primary =
    introOffer.displayCopy?.primary ||
    `${daysRemaining(introOffer.claimWindowEndsAt)} days left to claim`;
  const secondary =
    introOffer.displayCopy?.secondary ||
    `Claim by ${formatPhoenixDate(introOffer.claimWindowEndsAt)}`;
  const days = daysRemaining(introOffer.claimWindowEndsAt);
  const hours = hoursRemaining(introOffer.claimWindowEndsAt);

  const countdownLabel = isSubDay
    ? `${hours} ${hours === 1 ? "hour" : "hours"} left`
    : `${days} ${days === 1 ? "day" : "days"} left`;

  const ctaLabel = isSubDay
    ? "Claim now — expires today"
    : isUrgent
    ? "Claim now — expires today"
    : "Claim Intro Offer";

  // aria-label for the large countdown reads the full deadline (§3.6).
  const countdownAria = `Expires ${formatPhoenixDate(
    introOffer.claimWindowEndsAt
  )}, ${countdownLabel}`;

  return (
    <section
      className={`intro-offer-card intro-offer-card--eligible${
        isUrgent ? " intro-offer-card--urgent" : ""
      }`}
      aria-label="Intro offer status"
    >
      <div className="intro-offer-card__row">
        <span className="intro-offer-card__icon">
          <GiftIcon />
        </span>
        <h3 className="intro-offer-card__title">
          Your free intro session is waiting
        </h3>
      </div>
      {isUrgent ? (
        <>
          <p
            className="intro-offer-card__countdown-large"
            aria-label={countdownAria}
          >
            {countdownLabel}
          </p>
          <p className="intro-offer-card__subtitle">
            Claim by {formatPhoenixDate(introOffer.claimWindowEndsAt)}
          </p>
        </>
      ) : (
        <p className="intro-offer-card__subtitle">
          {secondary} — {primary}
        </p>
      )}
      <button
        type="button"
        className="intro-offer-card__cta"
        onClick={onClaim}
        disabled={claiming}
        aria-label="Claim your free intro session"
      >
        {claiming ? "Claiming..." : ctaLabel}
      </button>
      {claimError && (
        <p className="intro-offer-card__error" role="alert">
          {claimError}
        </p>
      )}
    </section>
  );
};

/**
 * Claimed state — use window open, no pending booking. §3.2.2 + §3.2.2b
 * emphasis when daysRemaining <= 1.
 */
const ClaimedState = ({ introOffer, isUrgent, onBrowse }) => {
  const claimedDate = formatPhoenixDate(introOffer.claimedAt);
  const useWindowDeadline = formatPhoenixDate(introOffer.expiresAt);
  const days = daysRemaining(introOffer.expiresAt);
  const useWindowSecondary =
    introOffer.displayCopy?.secondary || `Expires ${useWindowDeadline}`;

  const countdownLabel = `${days} ${days === 1 ? "day" : "days"} left`;

  return (
    <section
      className={`intro-offer-card intro-offer-card--claimed${
        isUrgent ? " intro-offer-card--urgent" : ""
      }`}
      aria-label="Intro offer status"
    >
      <div className="intro-offer-card__row">
        <span className="intro-offer-card__icon intro-offer-card__icon--success">
          <CheckIcon />
        </span>
        <h3 className="intro-offer-card__title">Intro offer claimed</h3>
      </div>
      <p className="intro-offer-card__subtitle">Claimed {claimedDate}</p>
      {isUrgent ? (
        <>
          <p
            className="intro-offer-card__countdown-large"
            aria-label={`Expires ${useWindowDeadline}, ${countdownLabel}`}
          >
            {countdownLabel}
          </p>
          <p className="intro-offer-card__subtitle">
            Book and attend by {useWindowDeadline}
          </p>
        </>
      ) : (
        <p className="intro-offer-card__use-window">
          Book and attend by {useWindowDeadline} — {countdownLabel}
        </p>
      )}
      <button
        type="button"
        className="intro-offer-card__cta"
        onClick={onBrowse}
        aria-label="Browse sessions to book your intro offer"
      >
        {isUrgent ? `Book today — expires ${useWindowDeadline}` : "Browse sessions"}
      </button>
      {introOffer.displayCopy?.primary && !isUrgent && (
        <p className="intro-offer-card__body" aria-hidden="true">
          {useWindowSecondary}
        </p>
      )}
    </section>
  );
};

/**
 * Claimed-with-pending-booking state — §3.2.3. Q4 nuance per feature doc:
 * the offer is NOT used until session-completion; "booked but not yet
 * attended" is its own visual state.
 */
const ClaimedWithBookingState = ({ introOffer, onViewBooking }) => {
  const bookingTime = formatPhoenixDateTime(introOffer.pendingBookingStartTime);
  return (
    <section
      className="intro-offer-card intro-offer-card--booked"
      aria-label="Intro offer status"
    >
      <div className="intro-offer-card__row">
        <span className="intro-offer-card__icon">
          <CalendarIcon />
        </span>
        <h3 className="intro-offer-card__title">Intro session booked</h3>
      </div>
      {bookingTime && (
        <p className="intro-offer-card__booking-time">{bookingTime}</p>
      )}
      <p className="intro-offer-card__body">
        Your intro offer applies to this booking.
      </p>
      <p className="intro-offer-card__body">
        Attend the session to complete your offer.
      </p>
      <button
        type="button"
        className="intro-offer-card__cta intro-offer-card__cta--secondary"
        onClick={onViewBooking}
        aria-label="View your intro session booking details"
      >
        View booking
      </button>
    </section>
  );
};

/**
 * Used state — terminal §3.2.4.
 */
const UsedState = ({ introOffer }) => {
  const usedDate = formatPhoenixDate(introOffer.usedAt);
  const primary = introOffer.displayCopy?.primary || "Intro offer used";
  return (
    <section
      className="intro-offer-card intro-offer-card--used"
      aria-label="Intro offer status"
    >
      <div className="intro-offer-card__row">
        <span className="intro-offer-card__icon intro-offer-card__icon--success">
          <CheckIcon />
        </span>
        <h3 className="intro-offer-card__title">{primary}</h3>
      </div>
      {usedDate && (
        <p className="intro-offer-card__subtitle">Completed {usedDate}</p>
      )}
      <p className="intro-offer-card__body">
        Thanks for joining us! Browse our other session options below.
      </p>
    </section>
  );
};

/**
 * Expired-unclaimed state — terminal §3.2.5.
 */
const ExpiredUnclaimedState = ({ introOffer }) => {
  const expiredDate = formatPhoenixDate(introOffer.claimWindowEndsAt);
  return (
    <section
      className="intro-offer-card intro-offer-card--expired"
      aria-label="Intro offer status"
    >
      <div className="intro-offer-card__row">
        <span className="intro-offer-card__icon intro-offer-card__icon--muted">
          <XIcon />
        </span>
        <h3 className="intro-offer-card__title">Intro offer expired</h3>
      </div>
      <p className="intro-offer-card__body">
        Your intro offer expired on {expiredDate} because it wasn't claimed
        within 7 days of sign-up.
      </p>
      <p className="intro-offer-card__body">
        Browse our other session options below.
      </p>
    </section>
  );
};

/**
 * Expired-unused state — terminal §3.2.6 + §3.2.6b post-cancel sub-variant.
 *
 * Per spec §3.2.6 + Scenario 7: when the BE exposes `cancelledBookingCount`
 * and it is > 0, render the sub-variant copy emphasizing the cancel. Otherwise
 * fall back to the unconditional §3.2.6 copy (Scenario 7 explicit fallback —
 * "When the BE does NOT expose cancelledBookingCount Then the card falls back
 * to 3.2.6 unconditional copy without error").
 */
const ExpiredUnusedState = ({ introOffer }) => {
  const claimedDate = formatPhoenixDate(introOffer.claimedAt);
  const expiredDate = formatPhoenixDate(introOffer.expiresAt);
  const isPostCancel =
    typeof introOffer.cancelledBookingCount === "number" &&
    introOffer.cancelledBookingCount > 0;
  return (
    <section
      className="intro-offer-card intro-offer-card--expired"
      aria-label="Intro offer status"
    >
      <div className="intro-offer-card__row">
        <span className="intro-offer-card__icon intro-offer-card__icon--muted">
          <XIcon />
        </span>
        <h3 className="intro-offer-card__title">Intro offer expired</h3>
      </div>
      {claimedDate && expiredDate && (
        <p className="intro-offer-card__subtitle">
          Claimed {claimedDate}; expired {expiredDate}
        </p>
      )}
      {isPostCancel ? (
        <p className="intro-offer-card__body">
          You used your intro offer for a booking that was cancelled. The intro
          offer does not refund or re-issue.
        </p>
      ) : (
        <p className="intro-offer-card__body">
          You claimed your intro offer but didn't complete a session before the
          deadline. The intro offer does not refund or re-issue.
        </p>
      )}
      <p className="intro-offer-card__body">
        Browse our other session options below.
      </p>
    </section>
  );
};

// -------------------------------------------------------------------------
// Top-level component
// -------------------------------------------------------------------------

/**
 * Customer-facing Intro Offer countdown card. Renders one of 5 lifecycle
 * states + 2 emphasis variants + §3.2.6b sub-variant.
 *
 * Graceful fallback: returns null when `introOffer` is absent (legacy
 * customer or feature flag OFF on BE) — Single Sessions page renders its
 * existing layout without the intro-offer block (spec §3.5 + Scenario 11).
 *
 * @param {object} props
 * @param {object} [props.introOffer]
 *   IntroOffer block from /api/profile (see tech doc §"GET /api/profile").
 *   Shape:
 *     - status / effectiveStatus  one of 'eligible' | 'claimed' | 'used' |
 *                                 'expired-unclaimed' | 'expired-unused'
 *     - eligibleAt, claimWindowEndsAt, claimedAt, expiresAt, usedAt  ISO 8601
 *     - pendingBookingId, pendingBookingStartTime  optional (Story C)
 *     - cancelledBookingCount  optional discriminator for §3.2.6b
 *     - displayCopy.{primary, secondary}  server-rendered Phoenix-MST strings
 *   Null / undefined / missing => render nothing.
 * @param {boolean} [props.loading=false]
 *   When true, render the skeleton state (parent still mounts the card to
 *   reserve layout space). Resolved by parent flipping loading -> false +
 *   passing the populated introOffer block.
 * @param {function} [props.onRefreshProfile]
 *   Called after a successful claim so the parent can re-fetch /api/profile
 *   and pass back the updated introOffer block (driving the cross-fade
 *   to the claimed state per Scenario 3).
 * @param {function} [props.onBrowseSessions]
 *   Click handler for the "Browse sessions" CTA on the claimed state.
 *   Defaults to no-op; parent typically scrolls to the session-type list.
 * @param {function} [props.onViewBooking]
 *   Click handler for the "View booking" CTA on the §3.2.3 booked state.
 */
const IntroOfferCard = ({
  introOffer,
  loading = false,
  onRefreshProfile,
  onBrowseSessions,
  onViewBooking,
}) => {
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState(null);

  if (loading) {
    return <SkeletonState />;
  }

  // Graceful fallback per spec §3.5 + Scenario 10 + Scenario 11. Render
  // nothing when the block is absent — the surrounding page is unaffected.
  if (!introOffer) {
    return null;
  }

  // Effective status is the canonical display driver (tech doc §"GET
  // /api/profile"). Falls back to raw status when the server hasn't
  // populated `effectiveStatus` (defensive).
  const status = introOffer.effectiveStatus || introOffer.status;

  const handleClaim = async () => {
    setClaiming(true);
    setClaimError(null);
    try {
      await introOfferAPI.claim();
      // Parent refreshes /api/profile to pick up the updated displayCopy +
      // status — drives the cross-fade per Scenario 3.
      if (typeof onRefreshProfile === "function") {
        await onRefreshProfile();
      }
    } catch (err) {
      const code = err?.response?.data?.code;
      const message =
        err?.response?.data?.message ||
        err?.userMessage ||
        "We couldn't claim your intro offer. Please try again.";
      // On CLAIM_WINDOW_EXPIRED / ALREADY_CLAIMED the BE has flipped state
      // defensively; refreshing the profile shows the updated card (DoD
      // bullets: "On-error (CLAIM_WINDOW_EXPIRED), card transitions to State
      // 3.2.5" + "On-error (ALREADY_CLAIMED), card refreshes to show current
      // state").
      if (
        code === "CLAIM_WINDOW_EXPIRED" ||
        code === "ALREADY_CLAIMED" ||
        code === "NOT_ELIGIBLE"
      ) {
        if (typeof onRefreshProfile === "function") {
          try {
            await onRefreshProfile();
          } catch (_refreshErr) {
            // Refresh failure is non-fatal — fall through to error copy.
          }
        }
        // For NOT_ELIGIBLE the legacy customer should not have seen this CTA
        // in the first place; show defensive copy from the BE.
        if (code === "NOT_ELIGIBLE") {
          setClaimError(message);
        }
      } else {
        setClaimError(message);
      }
    } finally {
      setClaiming(false);
    }
  };

  const handleBrowse = () => {
    if (typeof onBrowseSessions === "function") {
      onBrowseSessions();
    }
  };

  const handleViewBooking = () => {
    if (typeof onViewBooking === "function") {
      onViewBooking();
    }
  };

  // §3.2.3 takes precedence over §3.2.2/3.2.2b: claimed + pending booking
  // renders the booked state even if claimed. Q4 nuance from feature doc.
  if (status === "claimed" && introOffer.pendingBookingId) {
    return (
      <ClaimedWithBookingState
        introOffer={introOffer}
        onViewBooking={handleViewBooking}
      />
    );
  }

  if (status === "eligible") {
    const days = daysRemaining(introOffer.claimWindowEndsAt);
    const hours = hoursRemaining(introOffer.claimWindowEndsAt);
    const isUrgent = days <= 1;
    const isSubDay = hours <= 23 && hours > 0;
    return (
      <EligibleState
        introOffer={introOffer}
        isUrgent={isUrgent}
        isSubDay={isUrgent && isSubDay}
        onClaim={handleClaim}
        claiming={claiming}
        claimError={claimError}
      />
    );
  }

  if (status === "claimed") {
    const days = daysRemaining(introOffer.expiresAt);
    const isUrgent = days <= 1;
    return (
      <ClaimedState
        introOffer={introOffer}
        isUrgent={isUrgent}
        onBrowse={handleBrowse}
      />
    );
  }

  if (status === "used") {
    return <UsedState introOffer={introOffer} />;
  }

  if (status === "expired-unclaimed") {
    return <ExpiredUnclaimedState introOffer={introOffer} />;
  }

  if (status === "expired-unused") {
    return <ExpiredUnusedState introOffer={introOffer} />;
  }

  // Defensive: unknown status — render nothing (never crash the page).
  return null;
};

export default IntroOfferCard;
export {
  // Exported for unit-test access without going through the full top-level
  // wrapper (e.g., asserting the §3.2.6b copy branch in isolation).
  formatPhoenixDate,
  formatPhoenixDateTime,
  daysRemaining,
  hoursRemaining,
};
